import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Mail,
  FileText,
  TrendingUp,
  ArrowRight,
  Crown,
  Zap,
} from 'lucide-react';
import ToolLayout from '../../components/ToolLayout';
import { auth } from '../../lib/firebase';
import { toast } from 'sonner';
import { initializeRazorpaySubscription } from '../../lib/razorpayHandler';
import { useUser } from '../../context/UserContext';

const ToolsHub = () => {
  const { plan, loading, authReady, refreshUser } = useUser();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const navigate = useNavigate();

  if (loading || !authReady) {
    return (
      <ToolLayout>
        <div className="max-w-6xl mx-auto text-white text-center py-20">
          Loading your tools…
        </div>
      </ToolLayout>
    );
  }

  const tools = [
    {
      title: 'Email Generator',
      description: 'Create professional, personalized emails in seconds.',
      icon: Mail,
      path: '/tools/email',
    },
    {
      title: 'Content Brief Generator',
      description: 'Generate structured content briefs with clarity.',
      icon: FileText,
      path: '/tools/brief',
    },
    {
      title: 'Competitor Analysis',
      description: 'Get deep insights into your competition.',
      icon: TrendingUp,
      path: '/tools/competitor',
    },
  ];

  const planDetails = {
    free: {
      name: 'FREE',
      price: '$0 / month',
      description: 'Explore NT Studios AI tools',
      features: [
        'All AI tools included',
        '10 basic generations per day',
      ],
    },
    plus: {
      name: 'PLUS',
      price: '$19 / month',
      description: 'For solo founders & creators',
      features: [
        'All AI tools included',
        '50 basic generations per day',
        '15 deep research generations per month',
      ],
    },
    pro: {
      name: 'PRO',
      price: '$39 / month',
      description: 'For agencies & power users',
      features: [
        'All AI tools included',
        '100 basic generations per day',
        '50 deep research generations per month',
      ],
    },
  };

  const handleUpgrade = async (tier) => {
    const user = auth.currentUser;
    if (!user) {
      toast.error('Please login first');
      navigate('/tools/auth');
      return;
    }

    setProcessingPayment(true);

    await initializeRazorpaySubscription(
      tier,
      user,
      async () => {
        toast.success('Payment successful! Activating subscription…');
        setShowUpgrade(false);
        setTimeout(refreshUser, 1500);
      },
      (err) => {
        toast.error(err?.message || 'Payment failed');
      }
    );

    setProcessingPayment(false);
  };

  const activePlan = planDetails[plan] || planDetails.free;

  return (
    <ToolLayout>
      <div className="max-w-6xl mx-auto">

        {/* TOOLS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <Link
                key={tool.title}
                to={tool.path}
                className="rounded-2xl p-8 border border-white/10 bg-white/5 hover:bg-white/10 transition"
              >
                <Icon size={32} className="text-accent-teal mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">
                  {tool.title}
                </h3>
                <p className="text-neutral-silver mb-6">
                  {tool.description}
                </p>
                <span className="text-accent-teal inline-flex items-center gap-2 font-semibold">
                  Try Now <ArrowRight size={18} />
                </span>
              </Link>
            );
          })}
        </div>

        {/* ACTIVE PLAN */}
        <div className="rounded-2xl p-8 text-center bg-white/5 border border-white/10 mb-10">
          {(plan === 'plus' || plan === 'pro') && (
            <Crown className="mx-auto text-accent-teal mb-2" size={32} />
          )}

          <h2 className="text-2xl font-bold text-white mb-1">
            {activePlan.name} PLAN
          </h2>

          <p className="text-accent-teal font-semibold mb-2">
            {activePlan.price}
          </p>

          <p className="text-neutral-silver mb-4">
            {activePlan.description}
          </p>

          <ul className="text-neutral-silver text-sm space-y-1 mb-6">
            {activePlan.features.map((f, i) => (
              <li key={i}>• {f}</li>
            ))}
          </ul>

          {plan !== 'pro' && (
            <button
              onClick={() => setShowUpgrade(!showUpgrade)}
              className="inline-flex items-center gap-2 px-8 py-3 bg-accent-teal text-black rounded-lg font-semibold"
            >
              <Zap size={18} />
              {plan === 'free' ? 'Upgrade Plan' : 'Upgrade / Change Plan'}
            </button>
          )}
        </div>

        {/* INLINE UPGRADE CARDS */}
        {showUpgrade && (
          <div className="grid md:grid-cols-2 gap-8 p-8 rounded-2xl bg-neutral-charcoal border border-white/10">
            {/* PLUS */}
            <div className="rounded-xl p-6 bg-white/5 border border-white/10 text-center">
              <h3 className="text-2xl font-bold text-white mb-2">PLUS</h3>
              <p className="text-accent-teal text-3xl font-bold mb-4">
                $19/month
              </p>

              <ul className="text-neutral-silver text-sm space-y-1 mb-6">
                {planDetails.plus.features.map((f, i) => (
                  <li key={i}>• {f}</li>
                ))}
              </ul>

              <button
                disabled={processingPayment}
                onClick={() => handleUpgrade('plus')}
                className="w-full py-3 bg-accent-teal text-black rounded-lg font-semibold"
              >
                Upgrade to PLUS
              </button>
            </div>

            {/* PRO */}
            <div className="rounded-xl p-6 bg-white/5 border border-accent-teal/40 text-center">
              <h3 className="text-2xl font-bold text-white mb-2">PRO</h3>
              <p className="text-accent-teal text-3xl font-bold mb-4">
                $39/month
              </p>

              <ul className="text-neutral-silver text-sm space-y-1 mb-6">
                {planDetails.pro.features.map((f, i) => (
                  <li key={i}>• {f}</li>
                ))}
              </ul>

              <button
                disabled={processingPayment}
                onClick={() => handleUpgrade('pro')}
                className="w-full py-3 bg-accent-teal text-black rounded-lg font-semibold"
              >
                Upgrade to PRO
              </button>
            </div>
          </div>
        )}
      </div>
    </ToolLayout>
  );
};

export default ToolsHub;
